import { Euler, Float32BufferAttribute, Matrix4, Uint16BufferAttribute, Vector3 } from "three";
export var ToolType;
(function (ToolType) {
    ToolType[ToolType["line"] = 0] = "line";
    ToolType[ToolType["goniometer"] = 1] = "goniometer";
    ToolType[ToolType["osteotomy"] = 2] = "osteotomy";
    ToolType[ToolType["mechanicalAxis"] = 3] = "mechanicalAxis";
    ToolType[ToolType["contralateralMechanicalAxis"] = 4] = "contralateralMechanicalAxis";
    ToolType[ToolType["referencePoint"] = 5] = "referencePoint";
    ToolType[ToolType["plane"] = 6] = "plane";
    ToolType[ToolType["EOCCrop"] = 7] = "EOCCrop";
    ToolType[ToolType["plate"] = 8] = "plate";
    ToolType[ToolType["anatomicalAxis"] = 9] = "anatomicalAxis";
    ToolType[ToolType["grid"] = 10] = "grid";
    ToolType[ToolType["fitBone"] = 11] = "fitBone";
    ToolType[ToolType["insertionPoint"] = 12] = "insertionPoint";
    ToolType[ToolType["diaphysisAnatomicalAxis"] = 13] = "diaphysisAnatomicalAxis";
    ToolType[ToolType["blockingScrew"] = 14] = "blockingScrew";
    ToolType[ToolType["freeLine"] = 15] = "freeLine";
    ToolType[ToolType["referenceRing"] = 16] = "referenceRing";
    ToolType[ToolType["secondaryRing"] = 17] = "secondaryRing";
    ToolType[ToolType["ringGroup"] = 18] = "ringGroup";
    ToolType[ToolType["strut"] = 19] = "strut";
    ToolType[ToolType["orientationLine"] = 20] = "orientationLine";
})(ToolType || (ToolType = {}));
;
/**
 * Data type supported by the save/restore automatism.
 */
export var ToolStateDataType;
(function (ToolStateDataType) {
    ToolStateDataType[ToolStateDataType["primitive"] = 0] = "primitive";
    ToolStateDataType[ToolStateDataType["vector3"] = 1] = "vector3";
    ToolStateDataType[ToolStateDataType["euler"] = 2] = "euler";
    ToolStateDataType[ToolStateDataType["float32BufferAttribute"] = 3] = "float32BufferAttribute";
    ToolStateDataType[ToolStateDataType["uint16BufferAttribute"] = 4] = "uint16BufferAttribute";
    ToolStateDataType[ToolStateDataType["matrix4"] = 5] = "matrix4";
})(ToolStateDataType || (ToolStateDataType = {}));
/**
 * Utils to restore and save tools, used by the automatism
 */
export class ToolUtils {
    /**
     * Restores tools data.
     */
    static restore(obj, objToRestore) {
        for (const keysToSplit in objToRestore) {
            const value = objToRestore[keysToSplit];
            const keys = keysToSplit.split(".");
            let objToSet = obj;
            for (let i = 0; i < keys.length - 1; i++) {
                if (!objToSet)
                    break;
                objToSet = objToSet[keys[i]];
            }
            if (!objToSet)
                continue;
            const lastKey = keys[keys.length - 1];
            if (value.type == ToolStateDataType.vector3) {
                if (objToSet[lastKey] !== undefined) {
                    objToSet[lastKey].set(value.x, value.y, value.z);
                }
                else {
                    objToSet[lastKey] = new Vector3(value.x, value.y, value.z);
                }
            }
            else if (value.type == ToolStateDataType.euler) {
                if (objToSet[lastKey] !== undefined) {
                    objToSet[lastKey].set(value.x, value.y, value.z, value.order);
                }
                else {
                    objToSet[lastKey] = new Euler(value.x, value.y, value.z, value.order);
                }
            }
            else if (value.type == ToolStateDataType.float32BufferAttribute) {
                objToSet[lastKey] = new Float32BufferAttribute(value.array, value.itemSize);
            }
            else if (value.type == ToolStateDataType.uint16BufferAttribute) {
                objToSet[lastKey] = new Uint16BufferAttribute(value.array, value.itemSize);
            }
            else if (value.type == ToolStateDataType.matrix4) {
                if (objToSet[lastKey] !== undefined) {
                    objToSet[lastKey].set(...value.array).transpose();
                }
                else {
                    objToSet[lastKey] = new Matrix4().set(...value.array).transpose();
                }
            }
            else {
                objToSet[lastKey] = value;
            }
        }
    }
    /**
    * Gets tools data to save.
    */
    static getStateToRestore(obj) {
        if (obj) {
            const ret = {};
            this.recursiveGetStateToRestore(obj, "", ret);
            return {
                type: obj.toolType,
                data: ret
            };
        }
        return undefined;
    }
    static recursiveGetStateToRestore(obj, parentKey, ret) {
        if (obj) {
            if (obj.__saveKeys !== undefined) {
                for (const keysToSplit in obj.__saveKeys) {
                    const keys = keysToSplit.split(".");
                    let objToSave = obj;
                    for (const key of keys) {
                        objToSave = objToSave[key];
                        if (!objToSave) {
                            break;
                        }
                    }
                    if (objToSave) {
                        ret[`${parentKey}${keysToSplit}`] = this.createSingleObj(objToSave);
                    }
                }
            }
            if (obj.__saveKeysChildren !== undefined) {
                for (const key in obj.__saveKeysChildren) {
                    this.recursiveGetStateToRestore(obj[key], `${parentKey}${key}.`, ret);
                }
            }
        }
    }
    static createSingleObj(objToSave) {
        if (objToSave.isVector3) {
            return { x: objToSave.x, y: objToSave.y, z: objToSave.z, type: ToolStateDataType.vector3 }; //add enum type
        }
        if (objToSave.isEuler) {
            return { x: objToSave.x, y: objToSave.y, z: objToSave.z, order: objToSave.order, type: ToolStateDataType.euler };
        }
        if (objToSave instanceof Float32BufferAttribute) {
            return { itemSize: objToSave.itemSize, array: Array.from(objToSave.array), type: ToolStateDataType.float32BufferAttribute };
        }
        if (objToSave instanceof Uint16BufferAttribute) {
            return { itemSize: objToSave.itemSize, array: Array.from(objToSave.array), type: ToolStateDataType.uint16BufferAttribute };
        }
        if (objToSave instanceof Matrix4) {
            return { array: Array.from(objToSave.elements), type: ToolStateDataType.matrix4 };
        }
        //check errore se tipo non valido
        return objToSave;
    }
}
//# sourceMappingURL=ITool.js.map